import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { catalogApi } from '../lib/api';
import { inr } from '../mock';
import { useAuth } from '../context/AuthContext';
import { Package, Search, Sun, Zap, Wrench, Shield, Plus, Loader2, Pencil, Trash2, IndianRupee, ShieldCheck, BadgeCheck, Battery, Cable } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const TABS = [
  { key: 'panel',     label: 'Panels',      icon: Sun,     unit: 'per panel' },
  { key: 'inverter',  label: 'Inverters',   icon: Zap,     unit: 'per unit' },
  { key: 'battery',   label: 'Batteries',   icon: Battery, unit: 'per unit' },
  { key: 'cable',     label: 'Cables',      icon: Cable,   unit: 'per building' },
  { key: 'structure', label: 'Structure',   icon: Wrench,  unit: 'per kW' },
  { key: 'accessory', label: 'Accessories', icon: Shield,  unit: 'per unit' },
];

const EMPTY_FORM = {
  brand: '', model: '', price: '', unit: '', description: '',
  wattage: '', kw: '', kva: '', ah: '', phase: '',
  system_type: 'universal', structure_type: '', mnre_approved: false,
};

export default function BOM() {
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'Director' || user.role === 'Admin');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [activeTab, setActiveTab] = useState('panel');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await catalogApi.list();
      setItems(data);
    } catch (e) {
      toast.error('Failed to load catalog');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const activeTabDef = TABS.find(t => t.key === activeTab);

  const filtered = useMemo(() => items.filter(it =>
    it.type === activeTab &&
    (q === '' || (it.brand + ' ' + it.model).toLowerCase().includes(q.toLowerCase()))
  ), [items, activeTab, q]);

  const openAdd = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      unit: activeTabDef.unit,
      system_type: activeTab === 'inverter' ? 'on-grid'
                 : activeTab === 'battery' ? 'hybrid'
                 : 'universal',
    });
    setDialogOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      brand: it.brand || '', model: it.model || '',
      price: it.price ?? '',
      wattage: it.wattage ?? '', kw: it.kw ?? '',
      kva: it.kva ?? '', ah: it.ah ?? '',
      phase: it.phase || '',
      system_type: it.system_type || 'universal',
      structure_type: it.structure_type || '',
      unit: it.unit || activeTabDef.unit,
      description: it.description || '',
      mnre_approved: !!it.mnre_approved,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.brand.trim() || !form.model.trim() || form.price === '' || Number(form.price) < 0) {
      toast.error('Brand, model and a valid price are required');
      return;
    }
    if (activeTab === 'panel' && (!form.wattage || Number(form.wattage) <= 0)) {
      toast.error('Wattage (Wp) is required for panels'); return;
    }
    if (activeTab === 'inverter' && (!form.kw || Number(form.kw) <= 0)) {
      toast.error('Capacity (kW) is required for inverters'); return;
    }
    if (activeTab === 'battery' && !form.kva && !form.ah) {
      toast.error('kVA or Ah is required for batteries'); return;
    }

    const payload = {
      type: activeTab,
      brand: form.brand.trim(),
      model: form.model.trim(),
      price: Number(form.price),
      unit: form.unit || activeTabDef.unit,
      description: form.description || null,
      wattage: form.wattage ? Number(form.wattage) : null,
      kw: form.kw ? Number(form.kw) : null,
      kva: form.kva ? Number(form.kva) : null,
      ah: form.ah ? Number(form.ah) : null,
      phase: form.phase || null,
      system_type: form.system_type || null,
      structure_type: activeTab === 'structure' ? (form.structure_type || null) : null,
      active: true,
      mnre_approved: !!form.mnre_approved,
    };
    setSaving(true);
    try {
      if (editing) {
        const updated = await catalogApi.update(editing.id, payload);
        setItems(prev => prev.map(x => x.id === editing.id ? updated : x));
        toast.success(`${payload.brand} ${payload.model} updated`);
      } else {
        const created = await catalogApi.create(payload);
        setItems(prev => [...prev, created]);
        toast.success(`${payload.brand} ${payload.model} added`);
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await catalogApi.remove(confirmDel.id);
      setItems(prev => prev.filter(x => x.id !== confirmDel.id));
      toast.success('Item removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setConfirmDel(null); }
  };

  const subLine = (p, tabKey) => {
    if (tabKey === 'panel') return `${p.wattage}W Mono PERC · Tier-1`;
    if (tabKey === 'inverter') return `${p.kw} kW · ${p.phase ? (p.phase === 'single' ? '1φ' : '3φ') + ' · ' : ''}${p.system_type || 'on-grid'}`;
    if (tabKey === 'battery') return `${p.kva ? p.kva + ' kVA' : ''}${p.ah ? p.ah + ' Ah' : ''} · ${p.system_type || 'hybrid'}`;
    if (tabKey === 'cable') return p.description || p.model;
    return p.description || p.model;
  };

  return (
    <div className="space-y-6" data-testid="bom-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-emerald-950 flex items-center gap-2">
            <Package className="w-7 h-7 text-emerald-700" /> BOM Library
          </h1>
          <p className="text-slate-600 mt-1">
            {isAdmin ? 'Add, edit and price all components used across Inpace Power projects.' :
             'Browse the catalog. Ask a Director/Admin to add or edit items.'}
          </p>
        </div>
        {isAdmin && (
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={openAdd} data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-1" /> Add {activeTabDef.label.slice(0, -1)}
          </Button>
        )}
      </div>

      <Card className="p-4 border-emerald-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by brand or model…" className="pl-10" />
        </div>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-emerald-50 p-1 h-auto flex-wrap">
          {TABS.map(t => {
            const I = t.icon;
            return (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`} className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white">
                <I className="w-4 h-4 mr-1.5" />{t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {TABS.map(t => (
          <TabsContent key={t.key} value={t.key}>
            {loading ? (
              <div className="py-12 flex items-center justify-center text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading catalog…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                No {t.label.toLowerCase()} yet. {isAdmin && <button onClick={openAdd} className="text-emerald-700 font-semibold hover:underline">Add one →</button>}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                {filtered.map(p => {
                  const I = t.icon;
                  return (
                    <Card key={p.id} className="p-5 border-emerald-100 hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`bom-card-${p.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white flex items-center justify-center shrink-0">
                          <I className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">{p.brand}</Badge>
                            {p.mnre_approved && (
                              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">
                                <BadgeCheck className="w-3 h-3 mr-0.5" />MNRE
                              </Badge>
                            )}
                            {p.system_type && p.system_type !== 'universal' && (
                              <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 text-[10px]">{p.system_type}</Badge>
                            )}
                            {p.structure_type && (
                              <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 text-[10px]">{p.structure_type}</Badge>
                            )}
                          </div>
                          <div className="font-bold text-emerald-950 mt-1.5 truncate">{p.model}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{subLine(p, t.key)}</div>
                        </div>
                      </div>
                      <div className="mt-4 pt-3 border-t border-emerald-50 flex items-center justify-between">
                        <div>
                          <div className="text-xs text-slate-500">{p.unit || t.unit}</div>
                          <div className="text-xl font-extrabold text-emerald-900">{inr(p.price)}</div>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => setConfirmDel(p)} data-testid={`del-${p.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {!isAdmin && (
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-900 flex gap-2">
          <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Read-only view.</strong> Only <strong>Director</strong> or <strong>Admin</strong> roles can add, edit or remove catalog items.
          </div>
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle className="text-emerald-950">
              {editing ? `Edit ${activeTabDef.label.slice(0, -1)}` : `Add ${activeTabDef.label.slice(0, -1)}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Brand *</Label>
                <Input data-testid="form-brand" value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="e.g. Waaree" />
              </div>
              <div>
                <Label className="mb-1.5 block">Model *</Label>
                <Input data-testid="form-model" value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder="e.g. Aditya 540W" />
              </div>
            </div>

            {/* Panel specific */}
            {activeTab === 'panel' && (
              <div>
                <Label className="mb-1.5 block">Wattage (Wp) *</Label>
                <Input type="number" value={form.wattage} onChange={e => setForm(f => ({ ...f, wattage: e.target.value }))} placeholder="e.g. 540" />
              </div>
            )}

            {/* Inverter specific */}
            {activeTab === 'inverter' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block">Capacity (kW) *</Label>
                  <Input type="number" step="0.1" value={form.kw} onChange={e => setForm(f => ({ ...f, kw: e.target.value }))} placeholder="e.g. 5" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Phase</Label>
                  <Select value={form.phase || 'single'} onValueChange={v => setForm(f => ({ ...f, phase: v }))}>
                    <SelectTrigger data-testid="form-phase"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single-Phase</SelectItem>
                      <SelectItem value="three">Three-Phase</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Battery specific */}
            {activeTab === 'battery' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block">Capacity (kVA)</Label>
                  <Input type="number" step="0.5" value={form.kva} onChange={e => setForm(f => ({ ...f, kva: e.target.value }))} placeholder="e.g. 5 (for lithium)" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Capacity (Ah)</Label>
                  <Input type="number" value={form.ah} onChange={e => setForm(f => ({ ...f, ah: e.target.value }))} placeholder="e.g. 150 (for lead-acid)" />
                </div>
              </div>
            )}

            {/* System-type selector (inverter/battery/cable) */}
            {(activeTab === 'inverter' || activeTab === 'battery' || activeTab === 'cable') && (
              <div>
                <Label className="mb-1.5 block">System Type</Label>
                <Select value={form.system_type} onValueChange={v => setForm(f => ({ ...f, system_type: v }))}>
                  <SelectTrigger data-testid="form-system-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on-grid">On-Grid</SelectItem>
                    <SelectItem value="off-grid">Off-Grid</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    {activeTab !== 'battery' && <SelectItem value="universal">Universal (all systems)</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Structure-type selector (structure only) */}
            {activeTab === 'structure' && (
              <div>
                <Label className="mb-1.5 block">Structure Category</Label>
                <Select value={form.structure_type || 'low-rise'} onValueChange={v => setForm(f => ({ ...f, structure_type: v }))}>
                  <SelectTrigger data-testid="form-structure-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high-rise">High-Rise (&gt; 15m)</SelectItem>
                    <SelectItem value="mid-rise">Mid-Rise (3–5 floors)</SelectItem>
                    <SelectItem value="low-rise">Low-Rise (1–2 floors)</SelectItem>
                    <SelectItem value="flush-mount">Flush Mount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Price (₹) *</Label>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input data-testid="form-price" type="number" step="1" min="0" className="pl-9" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="e.g. 14500" />
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">Unit</Label>
                <Input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder={activeTabDef.unit} />
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block">Description (optional)</Label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. For RCC roofs upto 3m" />
            </div>

            {(activeTab === 'panel' || activeTab === 'inverter') && (
              <label className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-100 cursor-pointer">
                <Checkbox checked={!!form.mnre_approved} onCheckedChange={(v) => setForm(f => ({ ...f, mnre_approved: !!v }))} />
                <div>
                  <div className="text-sm font-semibold text-emerald-950 flex items-center gap-1.5">
                    <BadgeCheck className="w-4 h-4 text-emerald-700" /> MNRE-approved
                  </div>
                  <div className="text-xs text-slate-600">Include in the MNRE-listed catalog (required for PM Surya Ghar subsidy eligibility).</div>
                </div>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={save} disabled={saving} data-testid="form-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? 'Save changes' : 'Add item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDel?.brand} {confirmDel?.model}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove it from the catalog. Existing proposals that used this item are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
